/**
 * sl-9qm.4.3: Pagination Component
 *
 * Page navigation with page-size selector.
 * Emits page-change events via onNavigate callback.
 */
import { createElement, useState } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  ViewComponent,
  ViewComponentRenderProps,
} from '../../../types/view-component.types';
import * as styles from '../styles.css';

const configSchema = z.object({
  pageSizeOptions: z.array(z.number()).default([10, 25, 50]),
  defaultPageSize: z.number().default(10),
  showPageSizeSelector: z.boolean().default(true),
});

function PaginationRenderer({ config, data }: ViewComponentRenderProps) {
  const { pageSizeOptions, defaultPageSize, showPageSizeSelector } =
    config as z.infer<typeof configSchema>;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const totalItems = data.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);

  return createElement(
    'nav',
    {
      'aria-label': 'Pagination',
      style: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--sl-custom-app-gap-sm)',
        padding: 'var(--sl-custom-app-padding-md) 0',
        fontSize: 'var(--sl-custom-app-font-size-sm)',
      },
    },
    createElement(
      'button',
      {
        onClick: () => setCurrentPage(Math.max(1, safePage - 1)),
        disabled: safePage <= 1,
        style: {
          padding:
            'var(--sl-custom-app-gap-xxs) var(--sl-custom-app-padding-md)',
          border: '1px solid var(--streamlain-border-color)',
          borderRadius: 'var(--sl-custom-app-radius-sm)',
          background: 'var(--streamlain-background-primary-color)',
          cursor: safePage <= 1 ? 'default' : 'pointer',
          opacity: safePage <= 1 ? 0.5 : 1,
        },
        'aria-label': 'Previous page',
      },
      '←'
    ),
    createElement(
      'span',
      { style: { color: 'var(--streamlain-text-secondary-color)' } },
      `Page ${safePage} of ${totalPages}`
    ),
    createElement(
      'button',
      {
        onClick: () => setCurrentPage(Math.min(totalPages, safePage + 1)),
        disabled: safePage >= totalPages,
        style: {
          padding:
            'var(--sl-custom-app-gap-xxs) var(--sl-custom-app-padding-md)',
          border: '1px solid var(--streamlain-border-color)',
          borderRadius: 'var(--sl-custom-app-radius-sm)',
          background: 'var(--streamlain-background-primary-color)',
          cursor: safePage >= totalPages ? 'default' : 'pointer',
          opacity: safePage >= totalPages ? 0.5 : 1,
        },
        'aria-label': 'Next page',
      },
      '→'
    ),
    showPageSizeSelector &&
      createElement(
        'select',
        {
          value: pageSize,
          onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
            setPageSize(Number(e.target.value));
            setCurrentPage(1);
          },
          style: {
            marginLeft: 'var(--sl-custom-app-gap-sm)',
            padding:
              'var(--sl-custom-app-gap-xxs) var(--sl-custom-app-padding-sm)',
            border: '1px solid var(--streamlain-border-color)',
            borderRadius: 'var(--sl-custom-app-radius-sm)',
            background: 'var(--streamlain-background-primary-color)',
            fontSize: 'var(--sl-custom-app-font-size-sm)',
          },
          'aria-label': 'Items per page',
        },
        ...pageSizeOptions.map(size =>
          createElement('option', { key: size, value: size }, `${size} / page`)
        )
      )
  );
}

export const PaginationComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.PAGINATION,
  type: 'navigation',
  name: 'Pagination',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    '⟨⟩'
  ),
  capabilities: [],
  configSchema,
  defaultConfig: {
    pageSizeOptions: [10, 25, 50],
    defaultPageSize: 10,
    showPageSizeSelector: true,
  },
  render(props) {
    return createElement(PaginationRenderer, props);
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      '⟨⟩ Pagination'
    );
  },
};
